import { useEffect, useState } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import { ScoringConfigType } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

import { StyledRadioButton, StyledVisibilityModalTitle } from "./styles";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onNext: (configType: ScoringConfigType) => void;
};

const StyledVisibilityOption = styled(Flex)`
    cursor: pointer;
`;

const StyledVisibilityModalFooter = styled(Flex)`
    justify-content: flex-end;
    gap: 8px;
`;

export const ScoringVisibilityModal = ({ isOpen, onClose, onNext }: Props) => {
    const { translate } = useTranslate();
    const [selectedScope, setSelectedScope] = useState<ScoringConfigType>(ScoringConfigType.User);

    useEffect(() => {
        if (isOpen) {
            setSelectedScope(ScoringConfigType.User);
        }
    }, [isOpen]);

    const handleNext = () => onNext(selectedScope);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            data-automation="lead-score-scoring-visibility-modal"
            customStyles={{
                content: {
                    width: "400px",
                    maxWidth: "400px",
                    padding: "24px",
                },
            }}
        >
            <Stack gap="20px">
                <StyledVisibilityModalTitle fontVariant="h5_2">
                    {translate(leadsSearchFiltersI18nKeys.leadScoresVisibilityModalTitle())}
                </StyledVisibilityModalTitle>

                <Stack gap="20px">
                    <StyledVisibilityOption
                        alignItems="flex-start"
                        onClick={() => setSelectedScope(ScoringConfigType.Account)}
                        data-automation="lead-score-scoring-visibility-shared"
                    >
                        <StyledRadioButton
                            itemSelected={selectedScope === ScoringConfigType.Account}
                            onClick={() => setSelectedScope(ScoringConfigType.Account)}
                            renderLabel={() => (
                                <Stack
                                    gap="4px"
                                    margin="0 0 0 16px"
                                >
                                    <Text
                                        fontVariant="subtitle2"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {translate(leadsSearchFiltersI18nKeys.leadScoresVisibilitySharedTitle())}
                                    </Text>
                                    <Text
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon[300]}
                                    >
                                        {translate(leadsSearchFiltersI18nKeys.leadScoresVisibilitySharedDescription())}
                                    </Text>
                                </Stack>
                            )}
                        />
                    </StyledVisibilityOption>

                    <StyledVisibilityOption
                        alignItems="flex-start"
                        onClick={() => setSelectedScope(ScoringConfigType.User)}
                        data-automation="lead-score-scoring-visibility-private"
                    >
                        <StyledRadioButton
                            itemSelected={selectedScope === ScoringConfigType.User}
                            onClick={() => setSelectedScope(ScoringConfigType.User)}
                            renderLabel={() => (
                                <Stack
                                    gap="4px"
                                    margin="0 0 0 16px"
                                >
                                    <Text
                                        fontVariant="subtitle2"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {translate(leadsSearchFiltersI18nKeys.leadScoresVisibilityPrivateTitle())}
                                    </Text>
                                    <Text
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon[300]}
                                    >
                                        {translate(leadsSearchFiltersI18nKeys.leadScoresVisibilityPrivateDescription())}
                                    </Text>
                                </Stack>
                            )}
                        />
                    </StyledVisibilityOption>
                </Stack>

                <StyledVisibilityModalFooter>
                    <Button
                        type="flat"
                        onClick={onClose}
                        dataAutomation="lead-score-scoring-visibility-modal-cancel"
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleNext}
                        dataAutomation="lead-score-scoring-visibility-modal-next"
                    >
                        {translate("si.common.button.next")}
                    </Button>
                </StyledVisibilityModalFooter>
            </Stack>
        </Modal>
    );
};
